import {
  PermissionAction,
  PermissionScope,
  PermissionTableDataType,
} from 'dashboard/components/PermissionsTable/constants';
import { Permission } from 'lib/permissions';

export const CASE_DETAILS_ROW_NAME = 'Case details';

export const ProductsSectionData: PermissionTableDataType = {
  sections: [
    {
      label: 'Inquiries',
      subSections: [
        {
          scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
          rows: [
            {
              label: 'Inquiry details',
              tooltip:
                'Allows this role to create inquiries, see the list of all inquiries, and view individual inquiries',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.InquiryCreate,
                      scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.List,
                  permissions: [
                    {
                      permission: Permission.InquiryList,
                      scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.InquiryView,
                      scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.InquiryWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Inquiry templates',
              tooltip: 'Allows this role to create, view, and configure inquiry templates',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.InquiryTemplateCreate,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.InquiryTemplateView,
                      scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
                    },
                    {
                      permission: Permission.InquiryTemplateDocumentExtractionConfigView,
                      scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.InquiryTemplateWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
                    },
                    {
                      permission: Permission.InquiryTemplateDocumentExtractionConfigEdit,
                      scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Inquiry analytics',
              tooltip: 'Allows this role to view analytics for inquiries',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.InquiryAnalyticsView,
                      // James: Not all inquiry analytics controllers filter by template
                      scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Inquiry actions',
              tooltip: 'Allows this role to take actions (e.g. approve, decline) on inquiries',
              actions: [
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.InquiryDecision,
                      scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Inquiry resume',
              tooltip: 'Allows this role to resume inquiries',
              actions: [
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.InquiryResume,
                      scopes: [PermissionScope.Environment, PermissionScope.InquiryTemplate],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Verifications',
      subSections: [
        {
          scopes: [PermissionScope.Environment, PermissionScope.VerificationTemplate],
          rows: [
            {
              label: 'Verification details',
              tooltip:
                'Allows this role to see lists of verifications, view verification details and analytics, and take actions (e.g. tag) on verifications',
              actions: [
                {
                  label: PermissionAction.List,
                  permissions: [
                    {
                      permission: Permission.VerificationList,
                      scopes: [PermissionScope.Environment, PermissionScope.VerificationTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.VerificationView,
                      scopes: [PermissionScope.Environment, PermissionScope.VerificationTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.VerificationWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.VerificationTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Verification templates',
              tooltip: 'Allows this role to view and configure verification templates',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.VerificationTemplateView,
                      scopes: [PermissionScope.Environment, PermissionScope.VerificationTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.VerificationTemplateWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.VerificationTemplate],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Accounts',
      subSections: [
        {
          scopes: [PermissionScope.Environment, PermissionScope.AccountType],
          rows: [
            {
              label: 'Account details',
              tooltip:
                'Allows this role to create accounts, see lists of accounts, view individual accounts, and edit account information (e.g. name, DOB, address, email, phone number)',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.AccountCreate,
                      scopes: [PermissionScope.Environment, PermissionScope.AccountType],
                    },
                  ],
                },
                {
                  label: PermissionAction.List,
                  permissions: [
                    {
                      permission: Permission.AccountList,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.AccountView,
                      scopes: [PermissionScope.Environment, PermissionScope.AccountType],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.AccountWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.AccountType],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Account types',
              tooltip: 'Allows this role to create, view, and configure account types',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.AccountTypeCreate,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.AccountTypeView,
                      scopes: [PermissionScope.Environment, PermissionScope.AccountType],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.AccountTypeWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.AccountType],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Account actions',
              tooltip: 'Allows this role to create, view, and configure account actions',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.AccountActionCreate,
                      scopes: [PermissionScope.Environment, PermissionScope.AccountType],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.AccountActionView,
                      scopes: [PermissionScope.Environment, PermissionScope.AccountType],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.AccountActionWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.AccountType],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Transactions',
      subSections: [
        {
          scopes: [PermissionScope.Environment, PermissionScope.TransactionType],
          rows: [
            {
              label: 'Transaction details',
              tooltip:
                'Allows this role to see lists of transactions, view individual transaction details and analytics, and update transaction details and status',
              actions: [
                {
                  label: PermissionAction.List,
                  permissions: [
                    {
                      permission: Permission.TransactionList,
                      scopes: [PermissionScope.Environment, PermissionScope.TransactionType],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.TransactionView,
                      scopes: [PermissionScope.Environment, PermissionScope.TransactionType],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.TransactionWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.TransactionType],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Transaction types',
              tooltip:
                'Allows this role to create, view, and configure and archive transaction types',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.TransactionTypeCreate,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.TransactionTypeView,
                      scopes: [PermissionScope.Environment, PermissionScope.TransactionType],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.TransactionTypeWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.TransactionType],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Reports',
      subSections: [
        {
          scopes: [PermissionScope.Environment, PermissionScope.ReportTemplate],
          rows: [
            {
              label: 'Report details',
              tooltip:
                'Allows this role to create reports, see lists of reports, and edit report details',
              actions: [
                {
                  label: PermissionAction.List,
                  permissions: [
                    {
                      permission: Permission.ReportList,
                      scopes: [PermissionScope.Environment, PermissionScope.ReportTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.ReportCreate,
                      scopes: [PermissionScope.Environment, PermissionScope.ReportTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      // TODO: (jchang) Should be deprecated after multi-env launch.
                      permission: Permission.ReportAllView,
                      scopes: [PermissionScope.Environment, PermissionScope.ReportTemplate],
                    },
                    {
                      permission: Permission.ReportView,
                      scopes: [PermissionScope.Environment, PermissionScope.ReportTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Re-run',
              tooltip: 'Allows this role to re-run ad-hoc reports via Dashboard',
              actions: [
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.ReportWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.ReportTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Dismiss matches',
              tooltip: 'Allows this role to dismiss matches on reports',
              actions: [
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.ReportIgnoreHitsWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.ReportTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Pause or resume',
              tooltip: 'Allows this role to pause or resume a report that is set to recur',
              actions: [
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.ReportPauseWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.ReportTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Report templates',
              tooltip:
                'Allows this role to create, view, and configure individual report templates',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.ReportTemplateCreate,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.ReportTemplateView,
                      scopes: [PermissionScope.Environment, PermissionScope.ReportTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.ReportTemplateWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.ReportTemplate],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Cases',
      subSections: [
        {
          scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
          rows: [
            {
              label: CASE_DETAILS_ROW_NAME,
              tooltip:
                'Allows this role to create case details, see lists of cases, view individual case details, and resolve and take actions on cases',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.CaseCreate,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.List,
                  permissions: [
                    {
                      /*
                       * James: The checkbox will add case.all.list, but it can't be
                       * included by the default schema since it's optional.
                       */
                      permission: Permission.CaseList,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      /*
                       * James: The checkbox will add case.all.view, but it can't be
                       * included by the default schema since it's optional.
                       */
                      permission: Permission.CaseView,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.CaseWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Case templates',
              tooltip:
                'Allows this role to create, view, and configure (e.g. layout, actions, assignment policy, statuses) case templates',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.CaseTemplateCreate,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.CaseTemplateView,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.CaseTemplateWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Case actions',
              tooltip: 'Allows this role to view and configure case actions',
              actions: [
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.CaseActionCreate,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.CaseActionView,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.CaseActionWrite,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Assign',
              tooltip: 'Allows a role to assign a case to a particular user',
              actions: [
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.CaseAssign,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Bulk assign',
              tooltip: 'Allows this role to bulk-assign cases',
              actions: [
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.CaseBulkAssign,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Bulk resolve',
              tooltip: 'Allows this role to bulk-resolve cases',
              actions: [
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.CaseBulkWrite,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Case analytics',
              tooltip: 'Allows this role to view analytics for cases',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.CaseAnalyticsView,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseTemplate],
                    },
                  ],
                },
              ],
            },
            {
              label: 'SAR details',
              tooltip:
                'Allows this role to create, see lists of, view, and edit individual Suspicious Activity Reports (SARs)',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.SARCreate,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.List,
                  permissions: [
                    {
                      permission: Permission.SARList,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.SARView,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.SARWrite,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
              ],
            },
            {
              label: 'SAR export',
              tooltip:
                'Allows this role to export Suspicious Activity Reports (SARs) as XML or PDF',
              actions: [
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.SARExport,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
              ],
            },
            {
              label: 'SAR file',
              tooltip: 'Allows this role to e-file Suspicious Activity Reports (SARs)',
              actions: [
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.SARFile,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
              ],
            },
            {
              label: 'SAR configuration view',
              tooltip: 'Allows this role to edit the Filling Institution Details for SARs',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.SARConfigurationView,
                      scopes: [], // organization-level permission
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.SARConfigurationWrite,
                      scopes: [], // organization-level permission
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Case queues',
      subSections: [
        {
          scopes: [PermissionScope.Environment, PermissionScope.CaseQueue],
          rows: [
            {
              label: 'Case queues',
              tooltip: 'Allows this role to create and edit case queues.',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.CaseQueueView,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseQueue],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.CaseQueueWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseQueue],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Case assignment policies',
      subSections: [
        {
          scopes: [PermissionScope.Environment, PermissionScope.CaseAssignmentPolicy],
          rows: [
            {
              label: 'Case assignment policies',
              tooltip: 'Allows this role to configure case assignment policies.',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.CaseAssignmentPolicyView,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseAssignmentPolicy],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.CaseAssignmentPolicyWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.CaseAssignmentPolicy],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Workflows',
      subSections: [
        {
          scopes: [PermissionScope.Environment, PermissionScope.Workflow],
          rows: [
            {
              label: 'Workflow runs',
              tooltip: 'Allows this role to view all workflow runs',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.WorkflowRunList,
                      scopes: [PermissionScope.Environment, PermissionScope.Workflow],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Workflow details',
              tooltip:
                'Allows this role to create a workflow, view a workflow’s version history, and configure workflows',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.WorkflowCreate,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.WorkflowView,
                      scopes: [PermissionScope.Environment, PermissionScope.Workflow],
                    },
                    {
                      permission: Permission.WorkflowVersionList,
                      scopes: [PermissionScope.Environment, PermissionScope.Workflow],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.WorkflowWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.Workflow],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Graph',
      subSections: [
        {
          scopes: [PermissionScope.Environment],
          rows: [
            {
              label: 'Graph queries',
              tooltip:
                'Allows this role to view the results of graph queries and run new graph queries in Explorer',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.GraphQueryView,
                      scopes: [PermissionScope.Environment],
                    },
                    {
                      permission: Permission.GraphQueryWrite,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Graph query templates',
              tooltip:
                'Allows this role to create query templates, view query templates in a workflow or a case template, and edit or archive query templates',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.GraphQueryTemplateCreate,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.GraphQueryTemplateView,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.GraphQueryTemplateWrite,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Graph configuration',
              tooltip: 'Allows this role to view and edit details about a graph',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.GraphView,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.GraphWrite,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Integrations',
      tooltip: 'Allows this role to manage integrations from Marketplace',
      subSections: [
        {
          scopes: [PermissionScope.LegacyExternalIntegration],
          rows: [
            {
              label: 'Configurations',
              tooltip: 'Allows this role to manage existing connections from Persona Marketplace',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.ExternalIntegrationCreate,
                      scopes: [PermissionScope.LegacyExternalIntegration],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.ExternalIntegrationView,
                      scopes: [PermissionScope.LegacyExternalIntegration],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.ExternalIntegrationWrite,
                      scopes: [PermissionScope.LegacyExternalIntegration],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Integration Logs',
              tooltip: 'Allows this role to view and modify Integration logs',
              actions: [
                {
                  label: PermissionAction.List,
                  permissions: [
                    {
                      permission: Permission.ExternalIntegrationLogList,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.ExternalIntegrationLogView,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.ExternalIntegrationLogWrite,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Importers',
      tooltip: 'Allows this role to create and initiate imports to Persona',
      subSections: [
        {
          scopes: [PermissionScope.Environment],
          rows: [
            {
              label: 'Importers',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.ImporterPipelineView,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.ImporterPipelineWrite,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Lists',
      subSections: [
        {
          scopes: [PermissionScope.Environment, PermissionScope.List],
          rows: [
            {
              label: 'List details',
              tooltip: 'Allows this role to create, view, and rename and archive a list',
              actions: [
                {
                  label: PermissionAction.Create,
                  permissions: [
                    {
                      permission: Permission.ListCreate,
                      scopes: [PermissionScope.Environment],
                    },
                  ],
                },
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.ListView,
                      scopes: [PermissionScope.Environment, PermissionScope.List],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.ListWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.List],
                    },
                  ],
                },
              ],
            },
            {
              label: 'List items',
              tooltip: 'Allows this role to view and configure items in a list',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.ListItemView,
                      scopes: [PermissionScope.Environment, PermissionScope.List],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.ListItemWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.List],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      label: 'Table Views',
      tooltip:
        'Allows this role to configure a view (filters, columns, sort) on tables with saved views such as cases, API logs, and webhook events. (This changes what the table looks like to all users.)',
      subSections: [
        {
          scopes: [PermissionScope.Environment, PermissionScope.TableView],
          rows: [
            {
              label: 'List table views',
              actions: [
                {
                  label: PermissionAction.View,
                  permissions: [
                    {
                      permission: Permission.TableViewView,
                      scopes: [PermissionScope.Environment, PermissionScope.TableView],
                    },
                  ],
                },
                {
                  label: PermissionAction.Edit,
                  permissions: [
                    {
                      permission: Permission.TableViewWrite,
                      scopes: [PermissionScope.Environment, PermissionScope.TableView],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
